import * as React from 'react';
import { withCurrentStore } from 'store/currentStore';
import { useQueryData } from './use-query-data';
import { useUpdateQuery } from './use-update-query';

export function useMutation<TVariables, TData>(
  queryKey: string,
  opts: {
    fn: (variables: TVariables) => Promise<TData>;
  },
) {
  const updateQuery = useUpdateQuery(queryKey);
  const queryData = useQueryData<TData>(queryKey);

  const makeMutation = React.useCallback(
    async (
      variables: TVariables = {} as TVariables,
    ): Promise<TData | undefined> => {
      // we use this to prevent multiple simultaneous mutations because we are accessing the store directly and not through a hook which might be out of sync with the current store state
      const currentStoreQueryData = withCurrentStore(
        (store) => store.getState().query[queryKey],
      );

      if (currentStoreQueryData?.status === 'pending') return;

      updateQuery({ status: 'pending' });
      try {
        const data = await opts.fn(variables);
        updateQuery({ data, status: 'success' });
        return data;
      } catch (err: any) {
        updateQuery({ status: 'error', data: null });
      }
    },
    [opts, updateQuery, queryKey],
  );

  return {
    ...queryData,
    mutate: makeMutation,
  };
}
